'use client';

import { STATUS, SURFACE, BORDER } from '@/constants/colors';
import { memo } from 'react';
import Image from 'next/image';
import {
  Box,
  Stack,
  Typography,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Avatar,
  Divider,
  alpha,
} from '@mui/material';
import {
  Home,
  History,
  LocalShipping,
  AccountCircle,
  HelpOutline,
  Logout,
  Menu,
  ChevronLeft,
  Dashboard,
  LocationOn,
  BarChart,
  AccountBalanceWallet,
  Receipt,
  Gavel,
  Inventory2,
} from '@mui/icons-material';
import Link from 'next/link';
import { GetApp, CheckCircleOutline } from '@mui/icons-material';
import { themeColors } from '../theme/theme';
import { brandingConfig } from '@/config/branding';
import { usePWAInstall } from '../hooks/usePWAInstall';
import { useTranslation } from './useTranslation';
import { LanguageSwitch } from './LanguageSwitch';

const DESKTOP_SIDEBAR_WIDTH = 240;
const DESKTOP_SIDEBAR_COLLAPSED = 68;

/**
 * Desktop Sidebar Component - Collapsible sidebar for desktop layouts
 */
const DesktopSidebar = memo(
  ({ user, isExpanded = true, onToggleExpanded, onLogout, activeKey = '' }) => {
    const isBusiness = user?.userType?.toLowerCase() === 'business';
    const sidebarPrimaryColor = isBusiness ? '#f59e0b' : themeColors.primaryColor;
    const { canInstall, promptInstall, isInstalled } = usePWAInstall();
    const { t } = useTranslation();

    // Build navigation items based on user type AND business setup status
    let mainNavItems = [];

    if (isBusiness) {
      if (user?.businessSetupCompleted) {
        // Complete business profile - show all items
        mainNavItems = [
          {
            key: 'profile',
            label: t('nav.profile'),
            icon: AccountCircle,
            path: '/profile',
          },
          {
            key: 'business-dashboard',
            label: t('nav.dashboard'),
            icon: Dashboard,
            path: '/business-dashboard',
          },
          {
            key: 'my-pricing',
            label: t('nav.myPricing'),
            icon: Receipt,
            path: '/my-pricing',
          },
          {
            key: 'bulk-booking',
            label: t('nav.bulkBooking'),
            icon: Inventory2,
            path: '/bulk-booking',
          },
          {
            key: 'saved-addresses',
            label: t('nav.addresses'),
            icon: LocationOn,
            path: '/saved-addresses',
          },
          {
            key: 'activeParcels',
            label: t('nav.active'),
            icon: LocalShipping,
            path: '/activeParcels',
          },
          { key: 'history', label: t('nav.history'), icon: History, path: '/history' },
          {
            key: 'invoices',
            label: t('nav.invoices'),
            icon: Receipt,
            path: '/invoices',
          },
          {
            key: 'analytics',
            label: t('nav.analytics'),
            icon: BarChart,
            path: '/business-analytics',
          },
        ];
      } else {
        // Incomplete business profile - ONLY show profile
        mainNavItems = [
          {
            key: 'profile',
            label: t('nav.profile'),
            icon: AccountCircle,
            path: '/profile',
          },
        ];
      }
    } else {
      // Individual users - show all items
      mainNavItems = [
        {
          key: 'profile',
          label: t('nav.profile'),
          icon: AccountCircle,
          path: '/profile',
        },
        { key: 'home', label: t('nav.home'), icon: Home, path: '/home' },
        {
          key: 'activeParcels',
          label: t('nav.active'),
          icon: LocalShipping,
          path: '/activeParcels',
        },
        { key: 'history', label: t('nav.history'), icon: History, path: '/history' },
        {
          key: 'invoices',
          label: t('nav.invoices'),
          icon: Receipt,
          path: '/invoices',
        },
      ];
    }

    const bottomNavItems = [
      { key: 'support', label: t('nav.support'), icon: HelpOutline, path: '/support' },
      {
        key: 'pilot-portal',
        label: 'Pilot Portal',
        icon: LocalShipping,
        path: brandingConfig.messages.pilotPortalUrl,
        isExternal: true,
      },
      {
        key: 'terms',
        label: t('nav.terms'),
        icon: Gavel,
        path: '/terms',
      },
    ];

    // Only add wallet for:
    // 1. Individual users (always)
    // 2. Business users with completed profiles
    const showWallet = !isBusiness || user?.businessSetupCompleted;
    if (showWallet) {
      bottomNavItems.unshift({
        key: 'wallet',
        label: t('nav.wallet'),
        icon: AccountBalanceWallet,
        path: '/wallet',
      });
    }

    const isActive = (key) => {
      if (activeKey === key) return true;
      if (key === 'business-dashboard' && activeKey === 'home') return true;
      return false;
    };

    return (
      <Box
        sx={{
          width: isExpanded ? DESKTOP_SIDEBAR_WIDTH : DESKTOP_SIDEBAR_COLLAPSED,
          height: '100vh',
          position: 'fixed',
          left: 0,
          top: 0,
          bgcolor: '#fff',
          borderRight: '1px solid #e5e7eb',
          transition: 'width 0.25s ease',
          zIndex: 1200,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        {/* Header */}
        <Stack
          direction="row"
          alignItems="center"
          justifyContent={isExpanded ? 'space-between' : 'center'}
          sx={{ p: 1.5, borderBottom: '1px solid #f3f4f6', minHeight: 56 }}
        >
          {isExpanded && (
            <Box
              sx={{
                height: 32,
                width: 130,
                position: 'relative',
                cursor: 'pointer',
              }}
              onClick={() => (window.location.href = '/')}
            >
              <Image
                src="/assets/terrakite-teal-logo.png"
                alt={brandingConfig.assets.logoAlt}
                fill
                style={{ objectFit: 'contain', objectPosition: 'left' }}
                priority
              />
            </Box>
          )}
          <IconButton
            onClick={onToggleExpanded}
            size="small"
            sx={{ color: '#6b7280' }}
            aria-label={isExpanded ? t('nav.collapseSidebar') : t('nav.expandSidebar')}
          >
            {isExpanded ? <ChevronLeft fontSize="small" /> : <Menu fontSize="small" />}
          </IconButton>
        </Stack>

        {/* User Info */}
        {user && isExpanded && (
          <Box sx={{ p: 1.5, borderBottom: '1px solid #f3f4f6' }}>
            <Link href="/profile" passHref legacyBehavior>
              <Stack
                component="a"
                direction="row"
                spacing={1.5}
                alignItems="center"
                sx={{
                  cursor: 'pointer',
                  textDecoration: 'none',
                  borderRadius: 2,
                  p: 1,
                  mx: -1,
                  '&:hover': { bgcolor: '#f9fafb' },
                }}
              >
                <Avatar
                  src={user.avatar}
                  sx={{
                    width: 36,
                    height: 36,
                    bgcolor: sidebarPrimaryColor,
                    fontSize: '0.9rem',
                  }}
                >
                  {user.userName?.[0] || 'U'}
                </Avatar>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography
                    sx={{
                      fontSize: '0.85rem',
                      fontWeight: 700,
                      color: '#1f2937',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {user.userName || 'User'}
                  </Typography>
                  <Typography sx={{ fontSize: '0.7rem', color: '#6b7280' }}>
                    {isBusiness ? t('nav.business') : t('nav.individual')}
                  </Typography>
                </Box>
              </Stack>
            </Link>
          </Box>
        )}

        {/* New Shipment Button */}
        {isExpanded && (
          <Box sx={{ p: 1.5 }}>
            <Link href="/booking" passHref legacyBehavior>
              <Box
                component="a"
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 1,
                  py: 1.5,
                  px: 2,
                  borderRadius: 2.5,
                  background: `linear-gradient(135deg, ${sidebarPrimaryColor} 0%, ${isBusiness ? '#d97706' : '#059669'} 100%)`,
                  color: '#fff',
                  fontWeight: 700,
                  fontSize: '0.9rem',
                  cursor: 'pointer',
                  textDecoration: 'none',
                  position: 'relative',
                  overflow: 'hidden',
                  boxShadow: `0 4px 15px ${alpha(sidebarPrimaryColor, 0.4)}`,
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: `0 6px 20px ${alpha(sidebarPrimaryColor, 0.5)}`,
                  },
                }}
              >
                <LocalShipping sx={{ fontSize: 20 }} />
                {t('nav.newShipment')}
              </Box>
            </Link>
          </Box>
        )}

        {/* Main Navigation */}
        <Box sx={{ flex: 1, overflowY: 'auto', py: 1 }}>
          <List disablePadding>
            {mainNavItems.map((item) => {
              const active = isActive(item.key);
              const isBulkBooking = item.key === 'bulk-booking';
              const bulkBookingPurple = '#8b5cf6';

              return (
                <ListItem key={item.key} disablePadding sx={{ px: 0.75 }}>
                  <Link href={item.path} passHref legacyBehavior prefetch>
                    <ListItemButton
                      component="a"
                      sx={{
                        borderRadius: 2,
                        mb: 0.25,
                        py: 1,
                        px: isExpanded ? 1.5 : 1,
                        minHeight: 44,
                        justifyContent: isExpanded ? 'flex-start' : 'center',
                        bgcolor: active ? alpha(sidebarPrimaryColor, 0.15) : 'transparent',
                        borderLeft: active
                          ? `3px solid ${sidebarPrimaryColor}`
                          : '3px solid transparent',
                        position: 'relative',
                        ...(isBulkBooking &&
                          !active && {
                            background: `linear-gradient(135deg, ${alpha(bulkBookingPurple, 0.12)} 0%, ${alpha(bulkBookingPurple, 0.06)} 100%)`,
                            border: `2px solid ${alpha(bulkBookingPurple, 0.25)}`,
                            boxShadow: `0 0 12px ${alpha(bulkBookingPurple, 0.25)}`,
                            '&::after': {
                              content: '"PRO"',
                              position: 'absolute',
                              top: -6,
                              right: isExpanded ? 8 : -6,
                              fontSize: '0.6rem',
                              fontWeight: 900,
                              color: '#fff',
                              bgcolor: bulkBookingPurple,
                              px: 0.75,
                              py: 0.25,
                              borderRadius: 1,
                              boxShadow: `0 2px 4px ${alpha(bulkBookingPurple, 0.3)}`,
                            },
                          }),
                        '&:hover': {
                          bgcolor: active ? alpha(sidebarPrimaryColor, 0.2) : alpha('#000', 0.04),
                          ...(isBulkBooking && {
                            boxShadow: `0 0 16px ${alpha(sidebarPrimaryColor, 0.35)}`,
                          }),
                        },
                        textDecoration: 'none',
                      }}
                    >
                      <ListItemIcon
                        sx={{
                          minWidth: isExpanded ? 36 : 'auto',
                          color: active
                            ? sidebarPrimaryColor
                            : isBulkBooking && !active
                              ? bulkBookingPurple
                              : '#6b7280',
                        }}
                      >
                        <item.icon sx={{ fontSize: 20 }} />
                      </ListItemIcon>
                      {isExpanded && (
                        <ListItemText
                          primary={item.label}
                          primaryTypographyProps={{
                            fontSize: '0.85rem',
                            fontWeight: active ? 700 : isBulkBooking && !active ? 700 : 500,
                            color: active
                              ? sidebarPrimaryColor
                              : isBulkBooking && !active
                                ? bulkBookingPurple
                                : '#374151',
                          }}
                        />
                      )}
                    </ListItemButton>
                  </Link>
                </ListItem>
              );
            })}
          </List>

          <Divider sx={{ my: 1 }} />

          <List disablePadding>
            {bottomNavItems.map((item) => (
              <ListItem key={item.key} disablePadding sx={{ px: 0.75 }}>
                <Link href={item.path} passHref legacyBehavior prefetch>
                  <ListItemButton
                    component="a"
                    sx={{
                      borderRadius: 2,
                      mb: 0.25,
                      py: 0.75,
                      px: isExpanded ? 1.5 : 1,
                      minHeight: 40,
                      justifyContent: isExpanded ? 'flex-start' : 'center',
                      bgcolor: isActive(item.key)
                        ? alpha(sidebarPrimaryColor, 0.15)
                        : 'transparent',
                      borderLeft: isActive(item.key)
                        ? `3px solid ${sidebarPrimaryColor}`
                        : '3px solid transparent',
                      '&:hover': {
                        bgcolor: isActive(item.key)
                          ? alpha(sidebarPrimaryColor, 0.2)
                          : alpha('#000', 0.04),
                      },
                      textDecoration: 'none',
                    }}
                  >
                    <ListItemIcon
                      sx={{
                        minWidth: isExpanded ? 36 : 'auto',
                        color: isActive(item.key) ? sidebarPrimaryColor : '#6b7280',
                      }}
                    >
                      <item.icon sx={{ fontSize: 18 }} />
                    </ListItemIcon>
                    {isExpanded && (
                      <ListItemText
                        primary={item.label}
                        primaryTypographyProps={{
                          fontSize: '0.8rem',
                          fontWeight: isActive(item.key) ? 600 : 500,
                          color: isActive(item.key) ? sidebarPrimaryColor : '#4b5563',
                        }}
                      />
                    )}
                  </ListItemButton>
                </Link>
              </ListItem>
            ))}
          </List>
        </Box>

        {/* Language Selector */}
        {isExpanded && (
          <Box sx={{ px: 1.5, pb: 1 }}>
            <LanguageSwitch variant="sidebar" />
          </Box>
        )}

        {/* Install App */}
        {(canInstall || isInstalled) && (
          <Box sx={{ px: isExpanded ? 1.5 : 0.75, pb: 1 }}>
            {isExpanded ? (
              <ListItemButton
                onClick={canInstall ? promptInstall : undefined}
                disabled={isInstalled}
                sx={{
                  borderRadius: 2,
                  py: 1,
                  px: 1.5,
                  border: `1px dashed ${isInstalled ? '#d1d5db' : sidebarPrimaryColor}`,
                  bgcolor: isInstalled ? 'transparent' : alpha(sidebarPrimaryColor, 0.04),
                  '&:hover': {
                    bgcolor: alpha(sidebarPrimaryColor, 0.1),
                  },
                  '&.Mui-disabled': {
                    opacity: 0.7,
                  },
                }}
              >
                <ListItemIcon
                  sx={{ minWidth: 36, color: isInstalled ? '#9ca3af' : sidebarPrimaryColor }}
                >
                  {isInstalled ? (
                    <CheckCircleOutline sx={{ fontSize: 18 }} />
                  ) : (
                    <GetApp sx={{ fontSize: 18 }} />
                  )}
                </ListItemIcon>
                <ListItemText
                  primary={isInstalled ? t('pwa.appInstalled') : t('pwa.installApp')}
                  primaryTypographyProps={{
                    fontSize: '0.8rem',
                    fontWeight: 600,
                    color: isInstalled ? '#9ca3af' : sidebarPrimaryColor,
                  }}
                />
              </ListItemButton>
            ) : (
              <IconButton
                onClick={canInstall ? promptInstall : undefined}
                disabled={isInstalled}
                size="small"
                sx={{
                  width: 40,
                  height: 40,
                  mx: 'auto',
                  display: 'flex',
                  color: isInstalled ? '#9ca3af' : sidebarPrimaryColor,
                  border: `1px dashed ${isInstalled ? '#d1d5db' : sidebarPrimaryColor}`,
                }}
                title={isInstalled ? t('pwa.appInstalled') : t('pwa.installApp')}
              >
                {isInstalled ? (
                  <CheckCircleOutline sx={{ fontSize: 18 }} />
                ) : (
                  <GetApp sx={{ fontSize: 18 }} />
                )}
              </IconButton>
            )}
          </Box>
        )}

        {/* Logout */}
        <Box sx={{ p: 0.75, borderTop: '1px solid #f3f4f6' }}>
          <ListItemButton
            onClick={onLogout}
            sx={{
              borderRadius: 2,
              py: 1,
              px: isExpanded ? 1.5 : 1,
              justifyContent: isExpanded ? 'flex-start' : 'center',
              color: '#dc2626',
              '&:hover': { bgcolor: alpha('#dc2626', 0.08) },
            }}
          >
            <ListItemIcon sx={{ minWidth: isExpanded ? 36 : 'auto', color: '#dc2626' }}>
              <Logout sx={{ fontSize: 18 }} />
            </ListItemIcon>
            {isExpanded && (
              <ListItemText
                primary={t('nav.signOut')}
                primaryTypographyProps={{ fontSize: '0.8rem', fontWeight: 600 }}
              />
            )}
          </ListItemButton>
        </Box>
      </Box>
    );
  }
);

DesktopSidebar.displayName = 'DesktopSidebar';

export { DESKTOP_SIDEBAR_WIDTH, DESKTOP_SIDEBAR_COLLAPSED };
export default DesktopSidebar;
